import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ScienceIcon from '@mui/icons-material/Science';
import { motion } from 'framer-motion';

const chemicals = [
  {
    id: 'antiscalant-ro',
    title: 'RO Antiscalant',
    subtitle: 'Scale Prevention for RO Systems',
    description: 'High-performance antiscalant specifically designed for Reverse Osmosis membrane systems.',
    image: '/images/products/antiscalant-ro.jpg',
    features: [
      'Prevents calcium carbonate scaling',
      'Controls calcium sulfate formation',
      'Disperses iron particles',
      'Membrane compatible',
      'pH range: 2-11'
    ],
    applications: [
      'RO Systems',
      'Desalination Plants',
      'Industrial Water Treatment'
    ]
  },
  {
    id: 'antiscalant-cooling',
    title: 'Cooling Tower Antiscalant',
    subtitle: 'Scale Control for Cooling Systems',
    description: 'Advanced formulation for scale prevention in cooling tower and heat exchange systems.',
    image: '/images/products/antiscalant-cooling.jpg',
    features: [
      'Prevents mineral deposits',
      'Corrosion inhibition',
      'Dispersant properties',
      'Broad spectrum control',
      'Temperature stable'
    ],
    applications: [
      'Cooling Towers',
      'Heat Exchangers',
      'Industrial Cooling Systems'
    ]
  },
  {
    id: 'antiscalant-boiler',
    title: 'Boiler Antiscalant',
    subtitle: 'Boiler Water Treatment',
    description: 'Specialized antiscalant for high-pressure boiler systems and steam generators.',
    image: '/images/products/antiscalant-boiler.jpg',
    features: [
      'High-pressure compatible',
      'Oxygen scavenging',
      'Sludge conditioning',
      'pH stabilization',
      'Heat stable formula'
    ],
    applications: [
      'Steam Boilers',
      'Power Plants',
      'Industrial Processes'
    ]
  },
  {
    id: 'membrane-cleaner',
    title: 'Membrane Cleaner',
    subtitle: 'Specialized Cleaning Solution',
    description: 'Effective cleaning solution for RO and UF membrane maintenance.',
    image: '/images/products/membrane-cleaner.jpg',
    features: [
      'Removes organic fouling',
      'Dissolves mineral scale',
      'Safe for membranes',
      'Biodegradable',
      'Quick acting formula'
    ],
    applications: [
      'RO Membranes',
      'UF Systems',
      'Membrane Maintenance'
    ]
  },
  {
    id: 'ph-control',
    title: 'pH Control Chemicals',
    subtitle: 'pH Adjustment Solutions',
    description: 'Precise pH control chemicals for water treatment applications.',
    image: '/images/products/ph-control.jpg',
    features: [
      'Accurate pH adjustment',
      'Buffer capacity',
      'System compatible',
      'Stable formulation',
      'Easy to dose'
    ],
    applications: [
      'Water Treatment',
      'Process Control',
      'Effluent Treatment'
    ]
  },
  {
    id: 'biocide',
    title: 'Biocide Treatment',
    subtitle: 'Microbial Control',
    description: 'Effective biocide solutions for controlling biological growth in water systems.',
    image: '/images/products/biocide.jpg',
    features: [
      'Broad spectrum activity',
      'Long-lasting protection',
      'System compatible',
      'Non-oxidizing formula',
      'Cost-effective'
    ],
    applications: [
      'Cooling Systems',
      'Industrial Water',
      'Process Water'
    ]
  }
];

const SpecialtyChemicals = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <Box sx={{ bgcolor: '#f8f9fa' }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '300px', md: '400px' },
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to right, rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0.45))',
            zIndex: 1
          }
        }}
      >
        <Box
          component="img"
          src="/images/products/chemicals-hero.jpg"
          alt="Specialty Chemicals"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Typography
            variant="h1"
            sx={{
              color: 'white',
              fontWeight: 700,
              fontSize: { xs: '2.5rem', md: '4rem' },
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              mb: 2
            }}
          >
            Specialty Chemicals
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              maxWidth: '800px',
              fontSize: { xs: '1.1rem', md: '1.3rem' },
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
            }}
          >
            Advanced chemical solutions for water treatment and scale prevention
          </Typography>
        </Container>
      </Box>

      {/* Products Grid */}
      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 8 } }}>
        <Grid container spacing={4}>
          {chemicals.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card
                  onMouseEnter={() => setHoveredCard(item.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  <Box sx={{ position: 'relative', paddingTop: '60%' }}>
                    <CardMedia
                      component="img"
                      image={item.image}
                      alt={item.title}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease',
                        ...(hoveredCard === item.id && {
                          transform: 'scale(1.05)'
                        })
                      }}
                    />
                  </Box>
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <ScienceIcon sx={{ color: 'primary.main' }} />
                      <Box>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            color: 'primary.main'
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: 'text.secondary',
                            mb: 2
                          }}
                        >
                          {item.subtitle}
                        </Typography>
                      </Box>
                    </Stack>
                    
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {item.description}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: 'primary.main',
                        mb: 1,
                        fontWeight: 600
                      }}
                    >
                      Key Features
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} mb={2}>
                      {item.features.map((feature, idx) => (
                        <Chip
                          key={idx}
                          label={feature}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(0, 127, 255, 0.1)',
                            color: 'primary.main',
                            '&:hover': {
                              bgcolor: 'rgba(0, 127, 255, 0.2)'
                            }
                          }}
                        />
                      ))}
                    </Stack>

                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: 'primary.main',
                        mb: 1,
                        fontWeight: 600
                      }}
                    >
                      Applications
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                      {item.applications.map((application, idx) => (
                        <Chip
                          key={idx}
                          label={application}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&:hover': {
                              bgcolor: 'rgba(0, 127, 255, 0.1)'
                            }
                          }}
                        />
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default SpecialtyChemicals; 